import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";

/**
 * OAuth callback landing page for M365 integration.
 * Microsoft redirects here after admin consent.
 * Extracts success/error from query params and redirects to the tenant's M365 page.
 */
export function M365Callback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(3);

  const success = searchParams.get("success") === "true";
  const companyId = searchParams.get("companyId");
  const error = searchParams.get("error");

  useEffect(() => {
    if (!success && !error) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (success && companyId) {
            navigate(`/tenants/${companyId}/m365`, { replace: true });
          } else {
            // Can't determine tenant — go to tenants list
            navigate("/tenants", { replace: true });
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [success, error, companyId, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="mx-auto max-w-md rounded-xl border border-border bg-surface p-8 text-center shadow-lg">
        {!success && !error && (
          <>
            <Loader2 size={40} className="mx-auto mb-4 animate-spin text-accent" />
            <h1 className="text-lg font-semibold text-text-primary">Processing...</h1>
            <p className="mt-2 text-sm text-text-muted">Completing Microsoft 365 authorization.</p>
          </>
        )}

        {success && (
          <>
            <CheckCircle size={40} className="mx-auto mb-4 text-green-500" />
            <h1 className="text-lg font-semibold text-text-primary">Microsoft 365 Connected</h1>
            <p className="mt-2 text-sm text-text-muted">
              Admin consent granted successfully. Your M365 integration is now active.
            </p>
            <p className="mt-4 text-xs text-text-muted">
              Redirecting in {countdown}s...
            </p>
            {companyId && (
              <button
                className="mt-3 text-sm text-accent hover:underline"
                onClick={() => navigate(`/tenants/${companyId}/m365`, { replace: true })}
              >
                Go to M365 Settings now
              </button>
            )}
          </>
        )}

        {error && (
          <>
            <AlertCircle size={40} className="mx-auto mb-4 text-red-500" />
            <h1 className="text-lg font-semibold text-text-primary">Connection Failed</h1>
            <p className="mt-2 text-sm text-red-400">{error}</p>
            <p className="mt-4 text-xs text-text-muted">
              Redirecting in {countdown}s...
            </p>
            <button
              className="mt-3 text-sm text-accent hover:underline"
              onClick={() => navigate("/tenants", { replace: true })}
            >
              Back to tenants
            </button>
          </>
        )}
      </div>
    </div>
  );
}
